/**
 * A compact row of numbers above a list.
 *
 * The reason the list pages read as empty was never really the spacing — it was
 * that a list page said nothing. You arrived at Users and got 20 rows of 240
 * with no sense of the shape of the set: how many are blocked, how many are
 * vendors, how many arrived this week. Every metric here is also a filter, so
 * the strip answers the question and then takes you to the rows behind it.
 *
 * Deliberately one short row, not a grid of tiles: the table is the content.
 */
import { Link } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

export type Metric = {
  key: string;
  label: string;
  /** Already formatted — this component does no number formatting. */
  value: string | number | undefined;
  hint?: string;
  icon?: LucideIcon;
  /** Applying this metric as a filter. Makes the tile a link. */
  to?: string;
  tone?: 'default' | 'primary' | 'warning' | 'destructive' | 'success';
  /** Renders a shimmer in place of the value. */
  loading?: boolean;
};

const tone_value: Record<string, string> = {
  default: 'text-foreground',
  primary: 'text-primary',
  warning: 'text-warning',
  destructive: 'text-destructive',
  success: 'text-success',
};

const tone_icon: Record<string, string> = {
  default: 'bg-muted text-muted-foreground',
  primary: 'bg-primary/10 text-primary',
  warning: 'bg-warning/12 text-warning',
  destructive: 'bg-destructive/10 text-destructive',
  success: 'bg-success/12 text-success',
};

export function MetricStrip({ metrics }: { metrics: Metric[] }) {
  return (
    <div
      className="console-panel grid grid-cols-2 divide-border sm:grid-cols-3 sm:divide-x lg:grid-cols-5"
      data-testid="metric-strip"
    >
      {metrics.map((m) => {
        const body = (
          <>
            {m.icon && (
              <span
                className={cn(
                  'flex h-7 w-7 shrink-0 items-center justify-center rounded-control',
                  tone_icon[m.tone ?? 'default'],
                )}
              >
                <m.icon className="h-3.5 w-3.5" />
              </span>
            )}
            <span className="flex min-w-0 flex-col">
              <span className="text-micro font-bold uppercase tracking-wide text-muted-foreground">
                {m.label}
              </span>
              {m.loading ? (
                <span className="relative mt-1 h-5 w-14 overflow-hidden rounded-pill bg-muted">
                  <span className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-foreground/[0.06] to-transparent" />
                </span>
              ) : (
                <span
                  className={cn(
                    'tabular font-heading text-xl font-bold leading-tight',
                    tone_value[m.tone ?? 'default'],
                  )}
                  data-testid={`metric-${m.key}`}
                >
                  {m.value ?? '—'}
                </span>
              )}
              {m.hint && (
                <span className="truncate text-micro text-muted-foreground">{m.hint}</span>
              )}
            </span>
          </>
        );

        const shell =
          'flex items-center gap-2.5 px-3 py-2.5 transition-colors';

        return m.to ? (
          <Link
            key={m.key}
            to={m.to}
            className={cn(shell, 'hover:bg-primary/[0.045]')}
            data-testid={`metric-link-${m.key}`}
          >
            {body}
          </Link>
        ) : (
          <div key={m.key} className={shell}>
            {body}
          </div>
        );
      })}
    </div>
  );
}
